import React, { useRef, useEffect } from "react"
import cx from "classnames"
import { useStaticQuery, graphql, Link } from "gatsby"
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock"
import { parseLink } from "../../../utils/urlParser"
import { Nav } from "./Nav"
import Arrow from "../../../assets/icons/arrow.inline.svg"

import * as s from "./Menu.module.scss"

const Menu = ({ mainMenu, isOpen, currentPage, setShowMenu, triggerRef }) => {
  const { wpPage, countryPicker, shopCategories } = useStaticQuery(graphql`
    query MenuQuery {
      shopCategories: allShopifyCollection {
        nodes {
          handle
          title
        }
      }

      wpPage: wpNodeWithTemplate(template: { templateName: { eq: "Home" } }) {
        ... on WpPage {
          acfHome {
            menuCtas {
              color
              link {
                url
              }
              subTitle
              title
              image {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      placeholder: BLURRED
                      quality: 80
                      formats: [WEBP]
                    )
                  }
                }
              }
            }
          }
        }
      }

      countryPicker: wpNodeWithTemplate(
        template: { templateName: { eq: "Home" } }
      ) {
        ... on WpPage {
          remoteCountryPicker {
            countryPickerTitle
            countryPickerOptions {
              url
              title
              flagImage {
                altText
                sourceUrl
              }
            }
          }
        }
      }
    }
  `)
  const _content = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const nav = _content.current;
    if (!nav) return;

    if (isOpen) {
      document.documentElement.style.overflow = "hidden";
      disableBodyScroll(nav);

      // Focus menu panel after DOM updates
      setTimeout(() => {
        nav.focus();
      }, 300);

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== "Tab") return;

        const focusableSelectors = [
          'a[href]',
          'button:not([disabled])',
          'textarea:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',');

        const focusableElements = Array.from(nav.querySelectorAll<HTMLElement>(focusableSelectors))
          .filter(el => el.offsetParent !== null && !el.hasAttribute('disabled'));

        if (focusableElements.length === 0) return;

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        // Use isSameNode for reliable comparison
        const isAtFirst = document.activeElement?.isSameNode(first);
        const isAtLast = document.activeElement?.isSameNode(last);

        if (e.shiftKey && isAtFirst) {
          e.preventDefault();
          setShowMenu(false);
          setTimeout(() => {
            triggerRef?.current?.focus();
          }, 300);
        }

        if (!e.shiftKey && isAtLast) {
          e.preventDefault();
          setShowMenu(false);
          setTimeout(() => {
            triggerRef?.current?.focus();
          }, 300);
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          setShowMenu(false);
          setTimeout(() => {
            triggerRef?.current?.focus();
          }, 300);
        }
      };

      nav.addEventListener("keydown", handleKeyDown);

      return () => {
        nav.removeEventListener("keydown", handleKeyDown);
        enableBodyScroll(nav);
        document.documentElement.style.overflow = "";
      };
    } else {
      enableBodyScroll(nav);
      document.documentElement.style.overflow = "";
      setTimeout(() => {
        triggerRef?.current?.focus();
      }, 300);
    }
  }, [isOpen, setShowMenu, triggerRef]);

  return (
      <nav 
        ref={_content}
        id="main-menu-panel" 
        tabIndex={-1} 
        aria-hidden={!isOpen ? 'true' : 'false'} 
        aria-label="Main"
        className={cx(s.Menu, {
          [s.Menu___isOpen]: isOpen,
        })}
      >
        <Nav
          mainMenuItems={mainMenu}
          currentPage={currentPage}
          setShowMenu={setShowMenu}
          countryPicker={countryPicker.remoteCountryPicker}
          wpPage={wpPage}
          isOpen={isOpen}
        />
      </nav>
  )
}

export default Menu
